// Records the car's path through a lap so the best valid lap can drive the
// live delta (and, later, the ghost car).

export interface LapSample {
  s: number;
  t: number;
  x: number;
  z: number;
  heading: number;
}

const SAMPLE_SPACING = 2; // metres

export class LapRecorder {
  private current: LapSample[] = [];

  /** `spacing`: metres between samples (races use more, to keep long races small). */
  constructor(private readonly spacing = SAMPLE_SPACING) {}

  best: LapSample[] | undefined;
  bestTime: number | undefined;

  startLap(): void {
    this.current = [];
  }

  record(sample: LapSample): void {
    const last = this.current[this.current.length - 1];
    if (last && sample.s - last.s < this.spacing) return;
    // Ignore samples that go backwards along the lap (spins, resets).
    if (last && sample.s < last.s) return;
    this.current.push(sample);
  }

  finishLap(time: number, valid: boolean): boolean {
    const improved = valid && (this.bestTime === undefined || time < this.bestTime);
    if (improved) {
      this.best = this.current;
      this.bestTime = time;
    }
    this.lastLap = this.current;
    this.current = [];
    return improved;
  }

  /** Use a previously saved best lap. */
  setBest(samples: LapSample[], time: number): void {
    this.best = samples;
    this.bestTime = time;
  }

  /** The samples of the lap just finished (valid until the next lap starts recording). */
  lastLap: LapSample[] = [];

  /** Where the best lap was at lap time t (for the ghost car). */
  bestPoseAt(t: number): { x: number; z: number; heading: number } | undefined {
    const b = this.best;
    if (!b || b.length < 2 || t < b[0]!.t || t > b[b.length - 1]!.t) return undefined;
    let lo = 0;
    let hi = b.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (b[mid]!.t <= t) lo = mid;
      else hi = mid;
    }
    const a = b[lo]!;
    const c = b[hi]!;
    const k = (t - a.t) / (c.t - a.t || 1);
    let dh = c.heading - a.heading;
    dh = Math.atan2(Math.sin(dh), Math.cos(dh));
    return { x: a.x + (c.x - a.x) * k, z: a.z + (c.z - a.z) * k, heading: a.heading + dh * k };
  }

  /** Time the best lap took to reach distance s, or undefined without a best lap. */
  bestTimeAt(s: number): number | undefined {
    const b = this.best;
    if (!b || b.length < 2) return undefined;
    let lo = 0;
    let hi = b.length - 1;
    if (s <= b[0]!.s) return b[0]!.t;
    if (s >= b[hi]!.s) return b[hi]!.t;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (b[mid]!.s <= s) lo = mid;
      else hi = mid;
    }
    const a = b[lo]!;
    const c = b[hi]!;
    const k = (s - a.s) / (c.s - a.s || 1);
    return a.t + (c.t - a.t) * k;
  }
}
